import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useUserProfile } from '@/hooks/useUserProfile';
import { UserInfo } from '@/services/types/common';
import { SubMemberType } from '@/utils/constants/sub-account';

export const useUserInfo = (isEdit?: boolean) => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const subAccountId = search.get('subaccount_id');
  const name = search.get('name');
  const memberType = search.get('memberType');
  const memberStatus = search.get('memberStatus') || '';
  const subAccountMemberTag = search.get('member_tag') || '';
  const isEarnFundAccount = memberType === SubMemberType.EARN_FUND_ACCOUNT;
  const isEntrustTrade =
    memberType === SubMemberType.TRUST_ACCOUNT || isEarnFundAccount;
  const editSubAccountId = search.get('subAccountId');
  const {
    loading,
    userInfo,
    isDemoAccount,
    is_copy_pro_sub_account,
    isCopyTrader,
    isIslamicAccount,
    isUTA2: curLoginAccountIsUta2,
  } = useUserProfile();
  const apiManagementId = search.get('apiManagementId') as string;
  // 是否是查询子账号
  const isSubAccount = useMemo(
    () => (userInfo as UserInfo)?.is_submember || !!subAccountId,
    [userInfo, subAccountId],
  );
  // 是否是子账号登陆
  const isSubAccountLogin = useMemo(
    () => !!(userInfo as UserInfo)?.is_submember,
    [userInfo],
  );

  const memberId = useMemo(
    () => subAccountId || (userInfo as UserInfo)?.id,
    [userInfo, subAccountId],
  );

  const hasGoogle2fa = useMemo(() => {
    return (userInfo as UserInfo)?.has_google2fa;
  }, [userInfo]);

  const emailVerified = useMemo(
    () => (userInfo as UserInfo)?.email_verified,
    [userInfo],
  );

  const isCopypro = useMemo(() => {
    return (
      search.get('memberType') === SubMemberType.COPY_PRO_ACCOUNT ||
      is_copy_pro_sub_account
    );
  }, [search, is_copy_pro_sub_account]);

  const backApiManagement = () => {
    let baseUrl = subAccountId
      ? '/app/user/subaccount-api-management'
      : '/app/user/api-management';

    const memberTag = subAccountMemberTag
      ? `&member_tag=${subAccountMemberTag}`
      : '';

    if (!isEdit) {
      if (subAccountId) {
        baseUrl += `?subaccount_id=${subAccountId}&name=${name}${memberTag}`;
        if (memberType) {
          baseUrl += `&memberType=${memberType}`;
        }
        if (memberStatus) {
          baseUrl += `&memberStatus=${memberStatus}`;
        }
        navigate(baseUrl);
      } else {
        navigate(baseUrl);
      }
      return;
    }
    if (editSubAccountId) {
      baseUrl += `?subaccount_id=${editSubAccountId}&name=${name}${memberTag}`;

      if (memberType) {
        baseUrl += `&memberType=${memberType}`;
      }
      if (memberStatus) {
        baseUrl += `&memberStatus=${memberStatus}`;
      }
      navigate(baseUrl);
    } else {
      navigate(baseUrl);
    }
  };

  const isUTA2 = useMemo(() => {
    if (subAccountId || editSubAccountId) {
      return subAccountMemberTag.includes('uta_inverse');
    }
    return curLoginAccountIsUta2;
  }, [
    curLoginAccountIsUta2,
    subAccountMemberTag,
    editSubAccountId,
    subAccountId,
  ]);

  // 普通子账号
  const isCommonSubAccount = useMemo(() => {
    if (!isSubAccountLogin) {
      const memberType = search.get('memberType');
      const isCopyTrade = subAccountMemberTag?.includes('copytrade');
      return (
        (memberType === SubMemberType.ORDINARY_ACCOUNT ||
          memberType === SubMemberType.TRUST_ACCOUNT) &&
        !isCopyTrade
      );
    }

    return (
      !isCopyTrader &&
      !is_copy_pro_sub_account &&
      !isDemoAccount &&
      !isIslamicAccount
    );
  }, [
    search,
    isSubAccountLogin,
    isCopyTrader,
    is_copy_pro_sub_account,
    isDemoAccount,
    isIslamicAccount,
    subAccountMemberTag,
  ]);

  return {
    subAccountId,
    loading,
    isEntrustTrade,
    memberId,
    editSubAccountId,
    apiManagementId,
    isSubAccount,
    hasGoogle2fa,
    emailVerified,
    isSubAccountLogin,
    backApiManagement,
    isDemoAccount,
    isCopypro,
    isUTA2,
    isCommonSubAccount,
    isEarnFundAccount,
  };
};
